import { useMemo } from 'react';
import type { RevenueBySkuSeriesResponse } from '@nadoutong/contracts';
import { usePricing } from '../../api/pricing.ts';
import { revenueWindowUnavailable } from '../../components/pricingErrors.ts';
import { useRevenueBySkuSeries, useThroughputBySkuSeries } from '../../api/revenue.ts';
import { useChannelModelsList } from '../../api/useChannelModels.ts';
import {
  usdMicrosFromCentsFallback,
  usdMicrosToCnyCents,
} from '../../components/formatters.ts';
import { useT } from '../../i18n/index.ts';
import { Skeleton } from '../../ui/index.ts';
import {
  GroupAggregateTable,
  GroupTotalsStrip,
  GroupTrendsPanel,
} from './GroupSeriesSection.tsx';
import type { GroupAggregateRow, MetricsGroup } from './GroupSeriesSection.tsx';
import { planGroupSeries } from './hubState.ts';
import type { MetricsTabContext } from './hubState.ts';
import { ThroughputTrendsPanel } from './ThroughputSection.tsx';
import type { ThroughputGroup } from './ThroughputSection.tsx';

/**
 * 平台 SKU tab (#1675): full-fleet per-SKU revenue — window totals, a
 * sortable aggregate table, and an independent trend per SKU (no top-N,
 * per the operator's explicit ask). One `/by-sku/series` request feeds
 * all three sections: the series response carries per-group totals, so
 * the aggregate table is DERIVED from it instead of issuing a second
 * window-aggregate call (no `/v1/ops/revenue/by-sku` console route
 * exists, and fewer requests beats a redundant one).
 *
 * Consequence of that derivation: charged CNY and margin here use the
 * DISPLAY FX rate (`usePricing`), not the per-ledger-row market rate —
 * the table footnote states the basis. Cost and profit are ledger CNY
 * regardless.
 *
 * Display names join client-side from the marketplace SKU catalog
 * (`/v1/ops/channel-models`, admin-only like this whole tab); an
 * unknown `sku_id` (deleted SKU / legacy `'(unknown)'` bucket) falls
 * back to the raw series label. A failed catalog read only degrades
 * names, never the numbers.
 *
 * A second, independent `/by-sku/throughput-series` request (#1698)
 * feeds the RPM/TPM panel below the money trends — same plan (bounds +
 * granularity), separate loading/error state so a throughput failure
 * never degrades the money sections (or vice versa).
 */
export function PlatformSkuTab({ context }: { readonly context: MetricsTabContext }): JSX.Element {
  const t = useT();
  const { window, brand, range } = context;
  const plan = useMemo(() => planGroupSeries(window, range), [window, range]);
  const series = useRevenueBySkuSeries({
    granularity: plan.granularity,
    brand,
    from: plan.from,
    to: plan.to,
  });
  const throughput = useThroughputBySkuSeries({
    granularity: plan.granularity,
    brand,
    from: plan.from,
    to: plan.to,
  });
  const catalog = useChannelModelsList();
  const pricing = usePricing();
  const cnyPerUsd = pricing.data?.policy?.cny_per_usd ?? null;
  const rateValid = typeof cnyPerUsd === 'number' && Number.isFinite(cnyPerUsd) && cnyPerUsd > 0;
  const displayRate = rateValid ? cnyPerUsd : null;

  const nameBySkuId = useMemo(() => {
    const map = new Map<string, string>();
    for (const record of catalog.data ?? []) {
      map.set(record.sku_id, record.sku_name);
    }
    return map;
  }, [catalog.data]);

  const groups: readonly MetricsGroup[] = useMemo(() => {
    if (series.data === undefined) return [];
    return series.data.series.map((entry) => ({
      key: entry.label,
      name:
        entry.sku_id === null ? entry.label : nameBySkuId.get(entry.sku_id) ?? entry.label,
      series: entry,
    }));
  }, [series.data, nameBySkuId]);

  const aggregateRows = useMemo(
    () => (series.data === undefined ? [] : buildSkuAggregateRows(series.data, groups, displayRate)),
    [series.data, groups, displayRate],
  );

  const throughputGroups: readonly ThroughputGroup[] = useMemo(() => {
    if (throughput.data === undefined) return [];
    return throughput.data.series.map((entry) => ({
      key: entry.label,
      name:
        entry.sku_id === null ? entry.label : nameBySkuId.get(entry.sku_id) ?? entry.label,
      series: entry,
    }));
  }, [throughput.data, nameBySkuId]);

  return (
    <div className="flex flex-col gap-6" data-testid="metrics-platform-sku-tab">
      {plan.clamped ? (
        <p
          className="m-0 rounded-lg border border-warn-line bg-warn-dim/30 p-3 text-body text-warn"
          data-testid="metrics-platform-sku-clamp-note"
        >
          {t('metrics.group.clamp_note')}
        </p>
      ) : null}
      {plan.granularity === 'hour' ? (
        <p className="m-0 text-micro text-paper-low" data-testid="metrics-platform-sku-hourly-note">
          {t('metrics.group.hourly_note')}
        </p>
      ) : null}
      {series.isPending ? (
        <>
          <Skeleton className="h-24" testId="metrics-platform-sku-loading" />
          <Skeleton className="h-56" />
        </>
      ) : series.isError ? (
        <p className="m-0 text-body text-bad-bright" data-testid="metrics-platform-sku-error">
          {t('metrics.group.error')}
        </p>
      ) : series.data !== undefined ? (
        <>
          <GroupTotalsStrip
            groups={groups}
            cnyPerUsd={displayRate}
            testIdPrefix="metrics-platform-sku"
          />
          <GroupAggregateTable
            caption={t('metrics.platform_sku.table.title')}
            sub={t('metrics.platform_sku.table.sub')}
            note={t('metrics.platform_sku.table.note')}
            rows={aggregateRows}
            emptyLabel={t('metrics.group.empty')}
            testIdPrefix="metrics-platform-sku"
          />
          <GroupTrendsPanel
            title={t('metrics.platform_sku.trends.title')}
            groups={groups}
            granularity={series.data.granularity}
            cnyPerUsd={displayRate}
            emptyLabel={t('metrics.group.empty')}
            testIdPrefix="metrics-platform-sku"
          />
        </>
      ) : null}
      {throughput.isPending ? (
        <Skeleton className="h-56" testId="metrics-platform-sku-throughput-loading" />
      ) : throughput.isError ? (
        <p
          className="m-0 text-body text-bad-bright"
          data-testid="metrics-platform-sku-throughput-error"
        >
          {revenueWindowUnavailable(throughput.error)
            ? t('metrics.throughput.window_unavailable')
            : t('metrics.throughput.error')}
        </p>
      ) : throughput.data !== undefined ? (
        <ThroughputTrendsPanel
          title={t('metrics.throughput.sku.title')}
          groups={throughputGroups}
          granularity={throughput.data.granularity}
          windowFrom={throughput.data.window.from}
          windowTo={throughput.data.window.to}
          emptyLabel={t('metrics.group.empty')}
          testIdPrefix="metrics-platform-sku"
        />
      ) : null}
    </div>
  );
}

/**
 * Aggregate rows from the series per-group totals. Charged CNY and the
 * derived margin need the display rate; without one they render `—`
 * (never a fake ¥0 / 0% — same withholding rule as the charts).
 */
function buildSkuAggregateRows(
  data: RevenueBySkuSeriesResponse,
  groups: readonly MetricsGroup[],
  cnyPerUsd: number | null,
): readonly GroupAggregateRow[] {
  return data.series.map((entry, index) => {
    const chargedCnyCents = usdMicrosToCnyCents(
      usdMicrosFromCentsFallback(entry.total_usd_charged_micros, entry.total_usd_charged_cents),
      cnyPerUsd,
    );
    const marginPct =
      chargedCnyCents !== null && chargedCnyCents > 0
        ? entry.total_l2_profit_cny_cents / chargedCnyCents
        : null;
    return {
      key: entry.label,
      name: groups[index]?.name ?? entry.label,
      rawId: entry.sku_id,
      usageCount: entry.total_usage_count,
      chargedCnyCents,
      costCnyCents: entry.total_cny_upstream_cost_cents,
      profitCnyCents: entry.total_l2_profit_cny_cents,
      marginPct,
      costMissingCount: entry.total_cost_missing_count,
    };
  });
}
